import { FC, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Table, Tooltip } from 'antd'
import { EmployeeDisplayFragment, ObjectiveDto } from '../../../../types'
import { retrieveCurrencyConversionRate } from '../../../../shared/currencyConversionUtil'
import { useCurrency } from '../../../../context/currency-context'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerText from '../../../../components/custom_components/Text'
import PerButton from '../../../../components/custom_components/Button'
import { useDateTime } from '../../../../shared/useDateTime'
import { useAuth } from '../../../../context/auth-context'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import { PerModal } from '../../../../components/custom_components/Modal'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import { computeFullname } from '../../../../shared/nameDisplayPreferencesUtil'
import { IconIndicator } from '../../../../components/custom_components/IconIndicator/IconIndicator'
import { closeOutdatedTargets } from '../../../../components/custom_components/TargetsModal/async'

interface Props {
  outdatedTargets: ObjectiveDto[]
  employee: EmployeeDisplayFragment
  meetingId: string
}

export const OutdatedTargetsComponent: FC<Props> = ({ outdatedTargets, employee, meetingId }) => {
  const { user } = useAuth()
  const [t] = useTranslation()
  const { formatDateShort } = useDateTime(user)
  const { formatCurrency, calculateAndFormatNewTargetAmount } = useAmountAndCurrency(user.locale)
  const { currency: employeeCurrentContractCurrency, currencyConversionData } = useCurrency()

  const [modalVisible, toggleModalVisible] = useState<boolean>(false)
  const employeeFullName = computeFullname(
    user.nameDisplayPreference,
    employee.chosenFirstName || employee.legalFirstName,
    employee.chosenLastName || employee.legalLastName,
  )

  const tableColumns = [
    {
      key: 'title',
      title: t('okr.outdatedTargets'),
      width: '54%',
      render: (target: ObjectiveDto) => <PerText fontWeight="bold">{target.title}</PerText>,
    },
    {
      key: 'appraisalSeason',
      title: t('okr.objective.appraisalSeason'),
      width: '9%',
      className: 'textAlign-center',
      render: (target: ObjectiveDto) => {
        const year = new Date(target.start).getUTCFullYear()
        return <PerText>{year}</PerText>
      },
    },
    {
      key: 'monetaryBonus',
      title: t('okr.objective.monetary.bonus'),
      width: '13%',
      className: 'textAlign-right',
      render: (target: ObjectiveDto) => {
        const targetConversionRate: number | undefined =
          !!target.currency && !!currencyConversionData && target.currency !== employeeCurrentContractCurrency
            ? retrieveCurrencyConversionRate(target.currency, employeeCurrentContractCurrency, currencyConversionData)
            : undefined
        return (
          target.targetAmount && (
            <PerFlexRow direction="column">
              <Tooltip
                title={
                  target.currency !== employeeCurrentContractCurrency
                    ? targetConversionRate !== undefined
                      ? t('currencyConversion.tooltips.tableWarning')
                      : t('currencyConversion.tooltips.tableConversionWarning', {
                          startingCurrency: target.currency,
                          targetCurrency: employeeCurrentContractCurrency,
                        })
                    : undefined
                }
                placement="right"
              >
                <PerFlexRow>
                  <PerText appearance={'label-small'}>{formatCurrency(target.targetAmount, target.currency!)}</PerText>
                </PerFlexRow>
                {target.currency !== employeeCurrentContractCurrency && (
                  <PerFlexRow>
                    <PerText
                      appearance={'label-small'}
                      fontSize={'xs'}
                      mt="xxs"
                      color={targetConversionRate === undefined ? 'error' : undefined}
                      fontWeight={targetConversionRate === undefined ? 'bold' : undefined}
                    >
                      {calculateAndFormatNewTargetAmount(target.targetAmount, employeeCurrentContractCurrency, targetConversionRate)}
                    </PerText>
                  </PerFlexRow>
                )}
              </Tooltip>
            </PerFlexRow>
          )
        )
      },
    },
    {
      key: 'space',
      width: '4%',
    },
    {
      key: 'start',
      title: t('okr.objective.start'),
      width: '10%',
      render: (target: ObjectiveDto) => <PerText fontSize="xs">{formatDateShort(target.start, true)}</PerText>,
    },
    {
      key: 'due',
      title: t('okr.objective.due'),
      width: '10%',
      render: (target: ObjectiveDto) => <PerText fontSize="xs">{formatDateShort(target.due, true)}</PerText>,
    },
  ]

  return (
    <>
      <PerFlexRow justify="space-between" align="middle" mb="xs">
        <PerFlexRow align="middle">
          <PerText appearance="heading" color="error" mr="xs">
            {t('okr.instructions.manager.outdatedTargets')}
          </PerText>
          <IconIndicator icon="warning_fill" background="error" size="l" />
        </PerFlexRow>

        <PerButton
          text={t('global.button.close')}
          color="success"
          type="button"
          before="check_stroke"
          onClick={() => toggleModalVisible(true)}
        />
      </PerFlexRow>
      <PerText mb="xs" fontSize="s" color="secondary">
        {t('targetModal.outdatedTargets.disclaimer')}
      </PerText>

      <Table
        dataSource={outdatedTargets.map((change, index: number) => ({ ...change, key: index }))}
        columns={tableColumns}
        pagination={false}
        className={'small fullWidth'}
      />

      {modalVisible && (
        <OutdatedTargetsModal
          visible={modalVisible}
          onClose={() => toggleModalVisible(false)}
          employeeFullName={employeeFullName}
          meetingId={meetingId}
        />
      )}
    </>
  )
}

const OutdatedTargetsModal: FC<{ visible: boolean; onClose: () => void; employeeFullName: string; meetingId: string }> = ({
  visible,
  onClose,
  employeeFullName,
  meetingId,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleCloseOutdatedTargets = async () => {
    setIsLoading(true)
    setErrorMessage('')

    const result = await closeOutdatedTargets(meetingId)

    if (!!result.data && !result.errors) {
      resetAndClose()
      showNotification({
        type: 'success',
        message: t('targetModal.outdatedTargets.notifications.successTitle'),
        description: t('targetModal.outdatedTargets.notifications.successDescription'),
      })
    } else {
      const errorKey = result?.errors?.[0].extensions?.validationErrors?.[0]?.errorKey
      const errorMessage = errorKey?.startsWith('error.code')
        ? result?.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('targetModal.outdatedTargets.notifications.error')
        : t('targetModal.outdatedTargets.notifications.error')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  return (
    <PerModal
      title={{ heading: t('targetModal.outdatedTargets.title') }}
      size="medium"
      centered
      visible={visible}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleCloseOutdatedTargets,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <PerFlexRow direction="column">
        <PerText>
          <Trans i18nKey="targetModal.outdatedTargets.descriptionModal1" components={[<b></b>]} values={{ employeeFullName }} />
        </PerText>
        <PerText mt="s" fontWeight="bold">
          {t('targetModal.outdatedTargets.descriptionModal2', { employeeFullName: employeeFullName })}
        </PerText>

        {errorMessage && (
          <PerText color="error" mt="xs" fontSize="m">
            {errorMessage}
          </PerText>
        )}
      </PerFlexRow>
    </PerModal>
  )
}
